import React, {Component} from 'react';
import ReactDOM from "react-dom";
import PanelComponents from '../PanelComponents'
import PanelAddOns from '../PanelAddOns'
import {jsPDF} from 'jspdf';
import ProfileDisclaimers from './ProfileDisclaimers';
import FieldLevelHelp from '../components/FieldLevelHelp';

/**
 * Displays accordion lists of Orderable Items.
 *
 * Properties:
 * orderableItems - The OrderableItems object containing lists to display (recent, alphabetical, categories)
 * profileFilter - The search term to filter on
 * filteredOrderableItemsList - The filtered list of OrderableItems
 * expandedOrderableItemList - The id of the accordion that is expanded
 * onAccordionToggle - A function to call when the accordion header is clicked
 * onQuantityUpdate - A function to call when the quantity of an Orderable Item is changed
 * quantityMap - A map of Orderable Items with a quantity greater than 0
 * gsdlNo - The practitioner's gsdlNo
 * displayQuantity - Whether to display the quantity input on an OrderableItem
 * displayInfo - Whether to display the info icon on an OrderableItem
 * onPanelSelection - A function to call when an OrderableItem is clicked
 * selectedProduct - The name of the currently selected OrderableItem (For dropShip display)
 */
class OrderableItemList extends Component {
	constructor(props) {
		super(props);

		this.state = {
			orderableItemId: null,
			expandedOrderableItemList: this.props.expandedOrderableItemList
		}
	}

	/**
	 * Set the state's orderableItemId when an info icon is clicked
	 *
	 * @param event The HTML event that triggered the search. The orderableItemId to search will be the event target's dataset.id
	 */
	onOrderableItemInfo(event) {
		event.preventDefault();
		let orderableItemId = event.target.dataset.id;
		this.setState({'orderableItemId': orderableItemId});
	}

	render() {
		const hasOrderableItems =
			this.props.orderableItems &&
			(
				(this.props.orderableItems.otherOrderableItems && this.props.orderableItems.otherOrderableItems.length)
				||
				(this.props.orderableItems.recentlyOrderedItems && this.props.orderableItems.recentlyOrderedItems.length)
			);

		if( this.props.orderableItems == null ) {
			return <i className="fa fa-spinner fa-spin fa-fw loading-icon orderable-item-list-loading"></i>;
		} else if (!hasOrderableItems) {
			return <p className="margin-md text-danger">You are not eligible for online ordering.</p>;
		}

		let hasRecentlyOrderedItemsMatches = (this.props.orderableItems.recentlyOrderedItems || {}).length > 0;
		let hasOtherOrderedItemsMatches = (this.props.orderableItems.otherOrderableItems || {}).length > 0;
		let hasTopProducts = this.props.orderableItems.topProducts?.length > 0;

		return (
			<div>
				{this.state.orderableItemId && (
					<PanelComponentInfo 
						key={this.state.orderableItemId}
						orderableItemId={this.state.orderableItemId}
						gsdlNo={this.props.gsdlNo}
						modalHidden={() => this.setState({ orderableItemId: null })} />
				)}
				<div id="accordion" role="tablist">
					{this.props.bundleAccordion}
					{!this.props.profileFilter && <>
						{hasRecentlyOrderedItemsMatches && (
							<OrderableItemAccordion 
								heading='Recently Ordered' 
								id='recent' 
								expanded={this.state.expandedOrderableItemList === 'recent'} 
								orderableItems={this.props.orderableItems.recentlyOrderedItems}
								quantityMap={this.props.quantityMap}
								onAccordionToggle={this.props.onAccordionToggle}
								onQuantityUpdate={this.props.onQuantityUpdate} 
								onOrderableItemInfo={(evt) => this.onOrderableItemInfo(evt)}
								displayQuantity={this.props.displayQuantity} displayInfo={this.props.displayInfo}
								onPanelSelection={this.props.onPanelSelection}
								selectedProduct={this.props.selectedProduct} 
								dropShip={this.props.dropShip}
								practShipToOfficePermitted={this.props.practShipToOfficePermitted}
								shipToOffice={this.props.shipToOffice}
								displayAddRemove={this.props.displayAddRemove}
							/>
						)}
						{hasTopProducts && (
							<OrderableItemAccordion 
								heading="Genova's Most Popular Profiles"
								id='top-products' 
								expanded={this.state.expandedOrderableItemList === 'top-products' || (this.state.expandedOrderableItemList === 'recent' && !hasRecentlyOrderedItemsMatches)} 
								orderableItems={this.props.orderableItems.topProducts}
								quantityMap={this.props.quantityMap}
								onAccordionToggle={this.props.onAccordionToggle}
								onQuantityUpdate={this.props.onQuantityUpdate} 
								onOrderableItemInfo={(evt) => this.onOrderableItemInfo(evt)}
								displayQuantity={this.props.displayQuantity}
								displayInfo={this.props.displayInfo}
								onPanelSelection={this.props.onPanelSelection}
								selectedProduct={this.props.selectedProduct} 
								dropShip={this.props.dropShip}
								practShipToOfficePermitted={this.props.practShipToOfficePermitted}
								shipToOffice={this.props.shipToOffice}
								displayAddRemove={this.props.displayAddRemove}
							/>
						)}
						{Object.entries(this.props.orderableItems.categoryMap)
							.map( ([key,value], index) => {
								return (
									<OrderableItemAccordion key={key}
										heading={key} 
										id={key} 
										expanded={this.state.expandedOrderableItemList === key ||
											((this.state.expandedOrderableItemList === 'top-products'
											|| this.state.expandedOrderableItemList === 'recent')
											&& (index === 0 && !hasRecentlyOrderedItemsMatches && !hasTopProducts))} 
										orderableItems={value}
										quantityMap={this.props.quantityMap}
										onAccordionToggle={this.props.onAccordionToggle}
										onQuantityUpdate={this.props.onQuantityUpdate} 
										onOrderableItemInfo={(evt) => this.onOrderableItemInfo(evt)}
										displayQuantity={this.props.displayQuantity} displayInfo={this.props.displayInfo}
										onPanelSelection={this.props.onPanelSelection}
										selectedProduct={this.props.selectedProduct} 
										dropShip={this.props.dropShip}
										practShipToOfficePermitted={this.props.practShipToOfficePermitted}
										shipToOffice={this.props.shipToOffice}
										displayAddRemove={this.props.displayAddRemove}
									/>
								)
							}
						)}
						{hasOtherOrderedItemsMatches && (
							<OrderableItemAccordion 
								heading='Complete Alphabetical Listing' 
								id='other' 
								expanded={this.state.expandedOrderableItemList === 'other'} 
								orderableItems={this.props.orderableItems.otherOrderableItems}
								quantityMap={this.props.quantityMap}
								onAccordionToggle={this.props.onAccordionToggle}
								onQuantityUpdate={this.props.onQuantityUpdate} 
								onOrderableItemInfo={(evt) => this.onOrderableItemInfo(evt)}
								displayQuantity={this.props.displayQuantity}
								displayInfo={this.props.displayInfo}
								onPanelSelection={this.props.onPanelSelection}
								selectedProduct={this.props.selectedProduct} 
								dropShip={this.props.dropShip}
								practShipToOfficePermitted={this.props.practShipToOfficePermitted}
								shipToOffice={this.props.shipToOffice}/>
						)}
					</>}
					{this.props.profileFilter && (
						<OrderableItemAccordion 
							heading='Filtered Products' 
							id='filtered' 
							expanded={true} 
							orderableItems={this.props.filteredOrderableItemsList}
							quantityMap={this.props.quantityMap}
							onAccordionToggle={this.props.onAccordionToggle}
							onQuantityUpdate={this.props.onQuantityUpdate} 
							onOrderableItemInfo={(evt) => this.onOrderableItemInfo(evt)}
							displayQuantity={this.props.displayQuantity} displayInfo={this.props.displayInfo}
							onPanelSelection={this.props.onPanelSelection}
							selectedProduct={this.props.selectedProduct}
							dropShip={this.props.dropShip}
							practShipToOfficePermitted={this.props.practShipToOfficePermitted}
							shipToOffice={this.props.shipToOffice}
							covidRegistration={this.props.covidRegistration}
							displayAddRemove={this.props.displayAddRemove}
						/>
					)}
				</div>
			</div>
		);
	}
}

/**
 * Displays an accordion of Orderable Items.
 *
 * Properties:
 * heading - The title of the accordion
 * id - The accordion id, used to distinguish it from its siblings when collapsing/expanding
 * expanded - Whether this accordion is expanded
 * orderableItems - The list of OrderableItems to display
 * quantityMap - A map of Orderable Items with a quantity greater than 0
 * onAccordionToggle - A function to call when the accordion header is clicked
 * onQuantityUpdate - A function to call when the quantity of an Orderable Item is changed
 * displayQuantity - Whether to display the quantity input on an OrderableItem (Defaults to true)
 * displayInfo - Whether to display the info icon on an OrderableItem (Defaults to true)
 * onPanelSelection - A function to call when an OrderableItem is clicked
 * selectedProduct - The name of the selected OrderableItem
 */
const OrderableItemAccordion = ({heading, id, expanded, orderableItems, quantityMap, onAccordionToggle, onQuantityUpdate, onOrderableItemInfo, displayQuantity=true, displayInfo=true, onPanelSelection, selectedProduct, dropShip, shipToOffice, practShipToOfficePermitted, covidRegistration, displayAddRemove}) => {
	/**
	* Gets the quantity of an Orderable Item from the quantity map, or 0 if no quantity is found.
	* @param omsOrderableItemId The Orderable Item id for which to find the quantity
	*/
	const getItemQuantity = (omsOrderableItemId) => {
		const item = quantityMap.find( i => i.omsOrderableItemId === omsOrderableItemId );
		if( item && item.quantity ) {
			return item.quantity;
		}
		return 0;
	}

	if (!orderableItems) {
		return null;
	}

	const displayNYDisclaimer = (orderableItems.find( item => item.name.endsWith('*') ) ) &&
		<small><em style={{'float': 'right', 'fontWeight': 'normal'}}>* Indicates product cannot be ordered in the state of New York.</em></small>;

	// The style used for the Orderable Item table headings
	const headerStyle = {'position': 'sticky', 'top': '0', 'zIndex': '10', 'background': 'white', 'paddingTop': '5px', 'borderBottom': '1px solid black'};
	let backgroundHex = '#337ab7';
	if (dropShip) {
		backgroundHex = '#7DACA7';
	}
	if(covidRegistration) {
		backgroundHex = '#c54a4a';
	}
	
	return (
		<div className={"panel " + (shipToOffice ? 'panel-primary' : '')} style={{'marginBottom':'5px','borderColor':backgroundHex}}>
		   <div className="panel-heading" role="tab" id={'heading-' + id} style={{'padding': '0'}}>
			 <h4 className="panel-title" style={{'backgroundColor': backgroundHex}}>
			   <a role="button" 
				   className={expanded ? '' : 'collapsed'} 
				   data-toggle="collapse" 
				   data-parent="#accordion" 
				   href={'#collapse-' + id} 
				   aria-expanded={expanded} 
				   aria-controls={'collapse-' + id} 
				   style={{'padding': '10px 15px', 'backgroundColor': backgroundHex}}
				   onClick={(evt) => { onAccordionToggle && onAccordionToggle(id); }}
				>
				   <span className='fa fa-folder-collapse pr-10'></span>
				   {heading}
			   </a>
			 </h4>
		   </div>
			   <div id={'collapse-' + id} className={"panel-collapse collapse " + (expanded ? 'in' : '')} role="tabpanel" aria-labelledby={'heading-' + id}>
				 <div className="panel-body" style={{"maxHeight":"350px", "overflowY":"auto", "paddingTop": '0'}}>
					 { (!orderableItems || orderableItems.length === 0) ? 
						 <div style={{'paddingTop': '10px'}}>No matches found</div>
						 : <div>
							<table className="table-condensed table-hover" style={{'width': '100%', 'borderCollapse': 'separate'}}>
								<thead style={{'cursor': 'default', 'verticalAlign': 'bottom'}}>
									<tr>
										{displayQuantity && <th className="col-xs-1" style={headerStyle}>Qty</th>}
										{displayAddRemove && <th style={{...headerStyle, width: '1%'}}></th>}
										<th className="col-sm-3 col-md-2" style={headerStyle}>Category</th>
										<th className="col-sm-4 col-md-6" style={headerStyle}>
											{displayNYDisclaimer}
											Product
										</th>
										{displayInfo && <th className="col-xs-1" style={headerStyle}>Info</th>}
									</tr>	
								</thead>
								<tbody>
									{orderableItems.map( item => {
										return <OrderableItem 
												key={item.id} 
												orderableItem={item} 
												quantity={getItemQuantity(item.id)} 
												onQuantityUpdate={onQuantityUpdate} 
												onOrderableItemInfo={onOrderableItemInfo}
												displayQuantity={displayQuantity} displayInfo={displayInfo}
												onPanelSelection={onPanelSelection}
												selectedProduct={selectedProduct}
												shipToOffice={shipToOffice}
												practShipToOfficePermitted={practShipToOfficePermitted}
												dropShipOnly={item.dropShipOnly}
												displayAddRemove={displayAddRemove}
										/>
									} )}
								</tbody>
							</table>
						</div>
					}
				 </div>
			   </div>
		 </div>
	);
}

/**
 * Displays a single Orderable Item row
 */
const OrderableItem = ({orderableItem, quantity, onQuantityUpdate, onOrderableItemInfo, displayQuantity, displayInfo, onPanelSelection, selectedProduct, practShipToOfficePermitted, shipToOffice, dropShipOnly, displayAddRemove}) => {
	/**
	 * Maps an OrderableItem into a partial requisition to be stored in the quantity map.
	 */
	const constructRequisition = (orderableItem) => {
		return {
			id: undefined,
			omsOrderableItemId: orderableItem.id,
			omsOrderableItemName: orderableItem.name
		}
	}

	/**
	 * Calls the onPanelSelection function with the parameter of the orderable item id
	 * The onClick function will only be created if onPanelSelection is defined
	 */
	const onRowClick = () => {
		onPanelSelection( orderableItem.id );
	}

	const isDropShipOnly = !practShipToOfficePermitted && shipToOffice && dropShipOnly;

	return (
			<tr disabled={isDropShipOnly} onClick={onPanelSelection && onRowClick} style={{'backgroundColor':(selectedProduct && selectedProduct === orderableItem.name) ? 'khaki' : '', cursor: (!!onPanelSelection ? 'pointer' : 'default')}}>
				{displayQuantity && <td>
					<input disabled={isDropShipOnly} type="number" className="form-control" min='0' max='99' value={quantity} placeholder="0" style={{'padding': '6px', 'width': '80%', 'minWidth': '50px'}}
								 onChange={(evt)=>{evt.preventDefault();onQuantityUpdate({quantity: evt.target.value, item: constructRequisition(orderableItem)})}} onFocus={(event)=>event.target.select()} />
				</td>}
				{displayAddRemove && (
					<td>
					{!quantity ? (
						<button className="btn btn-link btn-unstyled" title="Add" style={{ minWidth: '0' }}>
							<span className="glyphicon glyphicon-plus"></span>
						</button>
					) : (
							<button className="btn btn-link btn-unstyled" title="Remove" style={{ minWidth: '0' }}>
								<span className="glyphicon glyphicon-ok text-success"></span>
							</button>
					)}
					</td>
				)}
				<td>{orderableItem.category}</td>
				<td>{orderableItem.name}</td>
				{(displayInfo && isDropShipOnly) && <td style={{pointerEvents: 'auto'}}><FieldLevelHelp textClassName="text-warning" iconClassName="fa-exclamation-triangle" helpText="This test is now exclusively offered as dropship testing. To place an order, please choose the 'Ship to Patient' option and provide the patient's address for direct shipment of the collection pack to the patient." large="true" /></td>}
				{(displayInfo && !isDropShipOnly) && <td><i data-id={orderableItem.id} className="text-info fa fa-question-circle" onClick={onOrderableItemInfo}></i></td>}
			</tr>
	);
}

/**
 * Displays the summary of Orderable Items with a quantity greater than 0
 */
const OrderableItemSummary = ( {quantityMap, onQuantityUpdate, onSubmit, onCustomize, overCanadaQtyLimit}) => {
	if( !quantityMap || Object.keys(quantityMap).length === 0 ) {
		return null;
	}
	return (
		<div className="panel panel-primary">
			<div className="panel-heading">
				<h4 className="panel-title">Summary</h4>
			</div>
			<div className="panel-body" style={{'padding': '0'}}>
				{onCustomize && <div style={{'padding': '10px'}}>Click the customize link to configure test selections.</div>}
				<table className="table-condensed table-hover" style={{'width': '100%', 'borderCollapse': 'separate'}}>
					<thead>
						<tr>
							<th className="col-xs-2" style={{'paddingTop': '5px', 'borderBottom': '1px solid black'}}>Qty</th>
							<th style={{'paddingTop': '5px', 'borderBottom': '1px solid black'}}>Product</th>
							<th className="col-xs-1" style={{'paddingTop': '5px', 'borderBottom': '1px solid black'}}></th>
						</tr>	
					</thead>
					<tbody>
						{quantityMap.map((item, index)=>(
							<tr key={item.omsOrderableItemId}>
								<td>
									<input 
										type="number" 
										className="form-control" 
										min='0' max='99' 
										value={item.quantity} 
										placeholder="0" 
										style={{'padding': '3px', 'width': '80%', 'minWidth': '50px'}}
										onChange={(evt)=>{evt.preventDefault();onQuantityUpdate({quantity: evt.target.value, item: item})}} 
										onFocus={(event)=>event.target.select()} />
								</td>
								<td>{item.omsOrderableItemName}</td>
								<td>
									<button type="button" title="Customize" className="btn-link btn-unstyled" onClick={()=>{onCustomize(item.omsOrderableItemId)}}>
										<i className="fa fa-pencil-square-o" />
									</button>
									<button type="button" title="Remove" className="btn-link btn-unstyled" onClick={()=>{onQuantityUpdate({quantity: 0, item: item})}}>
										<i className="text-danger fa fa-trash" />
									</button>
								</td>
							</tr>	
							)
						)}
					</tbody>
				</table>

				{overCanadaQtyLimit && (
					<div className="text-danger mt-10" style={{ padding: '5px' }}>
						<span className="fa fa-fw fa-warning" aria-hidden="true"></span> A maximum of 25 collection
						packs may be ordered online. If you need to order more packs, please call us at 800-522-4762.
					</div>
				)}
				
				
				<button type="submit" disabled={quantityMap.length === 0} title="Next" form="panels-form" className="btn btn-primary radius btn-sm pull-right mr-10" onClick={onSubmit}>
					Next
					<i className="pl-10 fa fa-arrow-circle-right"></i>
				</button>
			</div>
		</div>
	)
}

/**
 * A Modal component to display panel details
 */
class PanelComponentInfo extends Component {
	/**
	 * Constructor binds component functions.
	 */
	constructor(props) {
		super(props);
		this.state = {
			omsRequisition: null,
			loading: true
		}
		this.orderableItemPrintRef = React.createRef();

		this.searchByOrderableItem = this.searchByOrderableItem.bind(this);
	}

	/**
	 * Show the modal when the component mounts.
	 */
	componentDidMount() {
		window.$(ReactDOM.findDOMNode(this)).modal("show");
		window.$(ReactDOM.findDOMNode(this)).on("hidden.bs.modal", this.props.modalHidden)
		// bootstrap puts a blur on ".modal-open .page-wrapper" and recommends placing modals in a top-level to prevent
		// other CSS from affecting the modal appearance. We can't do this though, because the react root is nested
		// pretty deep in the wrapping DOM, so instead we're programatically dropping the blurry styles
		window.$(".page-wrapper").css({
			"filter": "none",
			"-webkit-filter": "none",
			"-moz-filter": "none",
			"-o-filter": "none",
			"-ms-filter": "none"
		});
		this.searchByOrderableItem();
	}

	/**
	 * Fetches the oms requisition structure for the orderable item.
	 *
	 * @param event The HTML event that triggered the search. The orderableItemId to search will be the event target's dataset.id
	 */
	searchByOrderableItem() {
		const orderableItemId = this.props.orderableItemId;
		const gsdlNo = this.props.gsdlNo;
		fetch(process.env.REACT_APP_GEAR_REST_URL + '/requisition/getReqByOrderableItem?orderableItemId=' + orderableItemId + '&gsdlNo=' + gsdlNo, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				return Promise.reject({response: response});
			}
		})
		.then(json => {
			this.setState({
				omsRequisition: json
			});
		})
		.catch(e => {
			this.setState({'showError': true})
		})
		.finally( () => this.setState({'loading': false}) );
	}

	handleOrderableItemPrintRef = async () => {
		const pdf = new jsPDF("portrait", "pt", "a3");
		const data = this.orderableItemPrintRef.current;

		pdf.html(data, {
			margin: 9,
			html2canvas: {
				scale: 0.80
			}
		}).then(() => {
			pdf.save("test-selection.pdf")
		})
	}

	/**
	 * Renders the orderable item info modal component.
	 */
	render() {
		return (
			<div className="modal fade" id="OrderableItemInfoModal" tabIndex="-1" role="dialog">
				<div className="modal-dialog modal-lg" role="document">
					<div className="modal-content">
						<div className="modal-header">
							<button type="button" className="close" data-dismiss="modal" aria-label="Close">
								<span aria-hidden="true">&times;</span>
							</button>
							{!this.state.omsRequisition && <h4 className="modal-title">{this.state.showError ? 'Error' : 'Loading Info...'}</h4>}
							{this.state.omsRequisition && <h4 className="modal-title">Info - {this.state.omsRequisition.orderableItem.name}</h4>}
						</div>
						<div className="modal-body">
							{this.state.omsRequisition && (
								<div>
									<div style={{width: "100%", display: "flex"}}>
										<h4 style={{width: "200rem"}}>This collection pack can be used for the following profiles / add-ons:</h4>
										<div style={{width: "100%", height: "100%", display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
											<button type="button" style={{alignSelf: "flex-end"}} className="btn-link btn-unstyled" onClick={this.handleOrderableItemPrintRef}><i className="fa fa-print padding-x-xs"></i></button>
										</div>
									</div>
									<form id="panels-form">
										<div className={"profile-list "} ref={this.orderableItemPrintRef}>
											{this.state.omsRequisition.orderableItem.profileList.map(function(listValue){
												return 	<div key={listValue.id}>
													<div>
														<label style={{'cursor': 'default'}}>
															<strong>{listValue.name}</strong>
														</label>
													</div>
													<PanelComponents
														components={listValue.profileComponents}
														disabled={true}
														selectedComponents={[]}
														selectedPanel={null}
														showHeader={true}
														showCptQuantity={this.state.omsRequisition.orderableItem.containsCptCodeQuantity}
														profile={listValue}
													/>
													<PanelAddOns
														requisitionResponses={this.state.omsRequisition.orderableItem}
														addOns={listValue.addOnList}
														disabled={true}
														selectedAddOns={[]}
														showCptQuantity={this.state.omsRequisition.orderableItem.containsCptCodeQuantity}
														shipToOffice={true}
													/>
													<ProfileDisclaimers omsInputField={listValue} />
												</div>
											}, this)}
										</div>
									</form>
								</div>
							)}
							{this.state.loading && (
								<div style={{'height': '100px', 'width': '75%', 'textAlign': 'center'}}>
									<i className="fa fa-spinner fa-spin fa-fw loading-icon"></i>
								</div>
							)}
							{this.state.showError && (
								<div className='text-danger'>An unexpected error has occurred. Please call us at 800-522-4762.</div>
							)}
						</div>
						<div className="modal-footer">
							<button type="button" className="btn btn-primary" data-dismiss="modal">Close</button>
						</div>
					</div>
				</div>
			</div>
		);
	};
}

export default OrderableItemList;
export {OrderableItemSummary};